import { IdWrapper } from './testTypes'

export const QuestionType = {
  OPEN_QUESTION: 'OPEN_QUESTION',
  MULTIPLE_CHOICE_QUESTION: 'MULTIPLE_CHOICE_QUESTION',
  MEMORY_TASK: 'MEMORY_TASK',
  LIKERT_SCALE_QUESTION: 'LIKERT_SCALE_QUESTION',
  ATTENTION_TASK: 'ATTENTION_TASK',
  SEQUENCING_TASK: 'SEQUENCING_TASK',
  BALLOON_ANALOGUE_RISK_TASK: 'BALLOON_ANALOGUE_RISK_TASK',
  IOWA_GAMBLING_TASK: 'IOWA_GAMBLING_TASK',
  GO_NO_GO_TASK: 'GO_NO_GO_TASK',
  IMAGE_MATCHING_TASK: 'IMAGE_MATCHING_TASK',
  COGNITIVE_FLEXIBILITY_TASK: 'COGNITIVE_FLEXIBILITY_TASK',
  LOGICAL_ODD_ONE_OUT_TASK: 'LOGICAL_ODD_ONE_OUT_TASK'
} as const

export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType]

export interface TestModuleQuestionResponse {
  id: IdWrapper
  testModuleId: IdWrapper
  questionType: QuestionType
  questionId: IdWrapper
  displayOrder: number
  attentionTask: AttentionTaskResponse | null
  balloonAnalogueRiskTask: BalloonAnalogueRiskTask | null
  cognitiveFlexibilityTask: CognitiveFlexibilityTask | null
  goNoGoTask: GoNoGoTask | null
  iowaGamblingTask: IowaGamblingTask | null
  likertScaleQuestion: LikertScaleQuestionResponse | null
  memoryTask: MemoryTask | null
  multipleChoiceQuestion: MultipleChoiceQuestion | null
  openQuestion: OpenQuestion | null
  imageMatchingTask: ImageMatchingTask | null
  sequencingTask: SequencingTask | null
  logicalOddOneOutTask: LogicalOddOneOutTask | null
}

export interface AttentionSymbol {
  index: number
  letter: string
  dashCount: number
  isTarget: boolean
}

export interface AttentionSymbolRow {
  rowIndex: number
  symbols: AttentionSymbol[]
}

export interface AttentionTaskResponse {
  id: IdWrapper
  rowCount: number
  symbolsPerRow: number
  timePerRowSeconds: number
  skillId: IdWrapper
  maxPoints: number
  symbols: AttentionSymbolRow[]
}

export interface Balloon {
  sequenceNumber: number
  burstAt: number
}

export interface BalloonAnalogueRiskTask {
  id: IdWrapper
  initialBurstProbabilityPercent: number
  finalBurstProbabilityPercent: number
  burstProbabilityPercent: number
  maxClicksBeforeBurst: number
  profitPerClick: number
  balloonCount: number
  skillId: IdWrapper
  pointsPerCurrencyUnit: number
  canHaveNegativeBalance: boolean
  balloons: Balloon[]
}

export interface CognitiveFlexibilityTask {
  id: IdWrapper
  additionListCount: number
  subtractionListCount: number
  alternatingListCount: number
  additionListTimeSeconds: number
  subtractionListTimeSeconds: number
  alternatingListTimeSeconds: number
  measureCorrectness: boolean
  skillId: IdWrapper
  maxPoints: number
  operations: {
    listIndex: number
    operationType: 'ADDITION' | 'SUBTRACTION'
    firstOperand: number
    secondOperand: number
    listType: 'ADDITION' | 'SUBTRACTION' | 'ALTERNATING'
    correctResult: number
  }[]
}

export interface GoNoGoTask {
  id: IdWrapper
  responseTimeSeconds: number
  goStimuliCount: number
  noGoStimuliCount: number
  stimuliFrequencySeconds: number
  skillId: IdWrapper
  maxPoints: number
  stimuli: {
    sequenceNumber: number
    isGoStimulus: boolean
  }[]
}

export interface IowaGamblingTask {
  id: IdWrapper
  z1DeckCardCount: number
  z2DeckCardCount: number
  d1DeckCardCount: number
  d2DeckCardCount: number
  z1DeckReward: number
  z2DeckReward: number
  d1DeckReward: number
  d2DeckReward: number
  z1DeckPenalty: number
  z2DeckPenalty: number
  d1DeckPenalty: number
  d2DeckPenalty: number
  z1DeckPenaltyProbabilityPercent: number
  z2DeckPenaltyProbabilityPercent: number
  d1DeckPenaltyProbabilityPercent: number
  d2DeckPenaltyProbabilityPercent: number
  totalChoicesCount: number
  skillId: IdWrapper
  generatedDecks: {
    deckId: string
    cards: {
      sequenceNumber: number
      incursPenalty: boolean
    }[]
  }[]
}

export interface LikertScaleQuestionResponse {
  id: IdWrapper
  questionText: string
  minLabel: string
  maxLabel: string
  timeLimitSeconds: number | null
  points: {
    id: IdWrapper
    questionId: IdWrapper
    skillId: IdWrapper
    degreeValue: number
    points: number
    imageFileId: string | null
    text: string | null
  }[]
}

export interface MemoryTask {
  id: IdWrapper
  digitCount: number
  memorizationTimeSeconds: number
  answerTimeSeconds: number
  responseMode: 'MANUAL' | 'CHOICE'
  choiceOptionsCount: number
  skillId: IdWrapper
  maxPoints: number
  generatedNumber: string
  choiceOptions: string[]
}

export interface MultipleChoiceQuestion {
  id: IdWrapper
  questionText: string
  requiredSelections: number
  timeLimitSeconds: number
  answers: {
    id: IdWrapper
    questionId: IdWrapper
    content: string
    skillId: IdWrapper
    points: number
  }[]
}

export interface OpenQuestion {
  id: IdWrapper
  questionText: string
  textFieldLength: number
  timeLimitSeconds: number
}

export interface ImageMatchingTask {
  id: IdWrapper
  title: string
  description: string
  mainImageFileId: IdWrapper
  matchingMode: 'DRAG' | 'SELECT'
  timeLimitSeconds: number
  skillId: IdWrapper
  maxPoints: number
  answers: {
    id: IdWrapper
    imageMatchingTaskId: IdWrapper
    answerImageFileId: IdWrapper
    isCorrectAnswer: boolean
  }[]
}

export interface SequencingTask {
  id: IdWrapper
  imageCount: number
  timeLimitSeconds: number
  sequencingMode: 'DRAG' | 'INPUT'
  skillId: IdWrapper
  maxPoints: number
  images: {
    id: IdWrapper
    sequencingTaskId: IdWrapper
    imageFileId: IdWrapper
    correctSequencePosition: number
  }[]
}

export interface LogicalOddOneOutImage {
  id: string
  logicalOddOneOutTaskId: string
  imageFileId: string
  isOddOneOut: boolean
}

export interface LogicalOddOneOutTask {
  id: string
  imageCount: number
  timeLimitSeconds: number
  skillId: string
  maxPoints: number
  images: LogicalOddOneOutImage[]
}
